import { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { Home, BookOpen, ChefHat, Calendar, Activity, Heart, User, Menu, X, Tag, PanelLeftClose, PanelLeft } from 'lucide-react';
import Footer from './Footer';
import styles from './Layout.module.css';

const navItems = [
  { to: '/', icon: Home, label: 'Home' },
  { to: '/cookbooks', icon: BookOpen, label: 'Cookbooks' },
  { to: '/recipes', icon: ChefHat, label: 'Recipes' },
  { to: '/meal-plan', icon: Calendar, label: 'Meal Plan' },
  { to: '/calories', icon: Activity, label: 'Calories' },
  { to: '/health-info', icon: Heart, label: 'Health Info' },
  { to: '/special-offers', icon: Tag, label: 'Special Offers' },
  { to: '/profile', icon: User, label: 'Profile' },
];

export default function Layout() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const location = useLocation();
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
      closeButtonRef.current?.focus();
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMenuOpen) {
        setIsMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMenuOpen]);

  const handleCloseMenu = () => {
    setIsMenuOpen(false);
    menuButtonRef.current?.focus();
  };

  return (
    <div className={`${styles.layout} ${isSidebarCollapsed ? styles.sidebarCollapsed : ''}`}>
      <header className={styles.mobileHeader}>
        <img src="/peapil-logo.png" alt="Peapil Health" className={styles.mobileLogoImage} />
        <button
          ref={menuButtonRef}
          className={styles.menuButton}
          onClick={() => setIsMenuOpen(true)}
          aria-label="Open navigation menu"
          aria-expanded={isMenuOpen}
          aria-controls="mobile-menu"
        >
          <Menu size={28} />
        </button>
      </header>

      {isMenuOpen && (
        <>
          <div
            className={styles.overlay}
            onClick={handleCloseMenu}
            aria-hidden="true"
          />
          <nav
            id="mobile-menu"
            className={styles.mobileDrawer}
            aria-label="Mobile navigation"
            role="dialog"
            aria-modal="true"
          >
            <div className={styles.drawerHeader}>
              <img src="/peapil-logo.png" alt="Peapil Health" className={styles.drawerLogo} />
              <button
                ref={closeButtonRef}
                className={styles.closeButton}
                onClick={handleCloseMenu}
                aria-label="Close navigation menu"
              >
                <X size={28} />
              </button>
            </div>
            <div className={styles.drawerNav}>
              {navItems.map(({ to, icon: Icon, label }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === '/'}
                  className={({ isActive }) =>
                    `${styles.drawerLink} ${isActive ? styles.drawerLinkActive : ''}`
                  }
                  onClick={handleCloseMenu}
                >
                  <Icon size={24} />
                  <span>{label}</span>
                </NavLink>
              ))}
            </div>
          </nav>
        </>
      )}

      <aside className={styles.sidebar}>
        <div className={styles.logo}>
          {!isSidebarCollapsed && (
            <img src="/peapil-logo.png" alt="Peapil Health" className={styles.logoImage} />
          )}
          <button
            className={styles.collapseButton}
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isSidebarCollapsed ? <PanelLeft size={22} /> : <PanelLeftClose size={22} />}
          </button>
        </div>
        <nav className={styles.nav}>
          {navItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              className={({ isActive }) =>
                `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`
              }
              title={isSidebarCollapsed ? label : undefined}
            >
              <Icon size={22} />
              {!isSidebarCollapsed && <span>{label}</span>}
            </NavLink>
          ))}
        </nav>
      </aside>
      <main className={styles.main}>
        <div className={styles.content}>
          <Outlet />
        </div>
        <Footer />
      </main>
    </div>
  );
}
